/* assets/vc-turntable.js — <vc-turntable>: the six-colourway turntable (spec D13, D14; task-15-brief.md). The markup, the data
 * and the CSS are sections/vc-turntable.liquid; the data rules (order, art, fabric, mood, link) are assets/vc-logic.js.
 *
 * Ported from prototypes/rose-b.html v4 ("no dome"), its script at :435-943, function by function: one projection for the
 * whole object (the platter is an ellipse and the figures stand ON it, so figures, doily, beads and names turn as one rigid
 * body), depth by atmosphere (opacity, contrast, saturation, a brightness floor) rather than blackening, the stepped
 * music-box plinth, the sheen / dust / halo layers, the rose window, the copy swap, the numeral index, the pause button,
 * pointer drag, the keyboard, and the ResizeObserver, IntersectionObserver and reduced-motion handling. The geometry and the
 * timing are the prototype's (DWELL 4.6s, TURN 1.9s, the 1.3s push-in); the maths below is its maths.
 * Changed (brief; controller rulings 1-15 of Task 15):
 *   - The colourways are the section's JSON through prepareTurntable(): numeral order, the metaobject's art merged over the
 *     prototype's values, the mood shown as Noir / Sweet, the fabric's lines joined by the gold dot. The prototype's `CW`
 *     table, its `?state=` `?front=` `?backdrop=` parameters (data-backdrop is the section's setting) and its SERIES lookup
 *     are gone; the front figure at rest is the third, as the prototype starts (the first when there are only two).
 *   - The CTA is a real link to the front colourway's collection, with its data-mood (Task 16's lace curtain hooks
 *     a[data-mood] clicks). Clicking it, the front figure or pressing Enter runs the prototype's push-in ("Now opening",
 *     1.3s), then follows the link with a real click on it, so the curtain (or the browser) takes it from there; a click with
 *     a modifier, or on a link that opens elsewhere, is the browser's. The prototype's demo return to the carousel and its
 *     `vcx:enter-chapter` event are gone: the page changes instead. Turning is held while the push-in runs, so the link
 *     followed is the colourway entered. A page restored from the back/forward cache is put back at rest (pageshow).
 *   - Under prefers-reduced-motion there is no autoplay, the turns are instant (the prototype's 1ms) and so is the push-in;
 *     the preference is read live (a change re-syncs the autoplay), not once. The button's Play still works: a visitor's own choice.
 *   - Layout: the prototype cuts to the stacked layout under 760px and, between 760 and 1100, lets the 700px theatre run off
 *     the stage. Here the stack begins under 960px (the theatre is then at least 80% of its size), and from 960 to 1096px
 *     the theatre is scaled to the room it has, standing on the stage's floor. The stacked layout is the prototype's.
 *   - The copy comes from an operator's data, so it is escaped (the prototype wrote its own strings with innerHTML); the
 *     "VI" of "III of VI" and of the numerals' names is the count of the colourways, not a constant.
 *   - The dust is seeded (DUST_SEED), so the motes are the same on every load.
 *   - Figures load with sizes / srcset from the JSON's "srcset" and "sizes" (the box is set from w and h, so the pick cannot move anything).
 *     The FRONT figure is printed by the section (Task 20, Parts 4 and 5), whole (.vc-tt__fig > .vc-tt__fi > pole, foot, <img>), placed where the
 *     first render() leaves it: the preload scanner finds the <img> in the HTML at high priority instead of after this module has run, and
 *     the figure is on the page before any script has. It is ADOPTED here IN PLACE (its nodes are kept and given the same numbers, never
 *     moved or made twice: it is the page's LCP element). Every other figure, and every reflection, is made lazy and at low priority: they
 *     are behind or beside the front one, and the reflections' boxes are display:none until their figure is in front.
 *   - The first frame is static (Task 20, Part 5): the section prints the copy of the front colourway (.vc-tt__v) and the platter and plinth
 *     too, and its CSS lays them out by container queries that repeat layout() below. The copy is adopted like the figure (no swap for it).
 *     KEEP THEM IN STEP: a constant (CX CY RX RT BAND GAP BASE RR H0 P0 CAMY), the art rules, buildInner(), setCopy() or layout() changed
 *     here is changed in sections/vc-turntable.liquid, whose first frame turntable_suite compares with the built stage.
 *   - The index (numerals, Pause) needs this module and is hidden until it has built the stage (.is-built); the stage takes the keyboard
 *     (tabindex) only then. It lets go of its observers and timers when it leaves the page and takes them up again if it is moved (the theme
 *     editor re-renders sections).
 * connectedCallback runs again when the element is moved or a section is re-rendered:
 * https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements
 */
// '@vc/logic': the import map's name (snippets/scripts.liquid) for the VERSIONED assets/vc-logic.js. A relative
// './vc-logic.js' drops this module's ?v= and loads the unversioned URL, which a returning visitor's browser keeps stale
// after an update (guard: tests/js/import-map.test.mjs).
import { escapeHtml, prepareTurntable, roman } from '@vc/logic';

const SVGNS = 'http://www.w3.org/2000/svg';
const TAU = Math.PI * 2;

/* One projection for the whole object (v4): the platter is an ellipse (a circle seen at RT = ry/rx), and the figures stand ON
   that same ellipse, so figures, doily, beads and names turn as one rigid body. Size falls off mildly and separately with
   depth: s = SMIN + (1 - SMIN) * d.  rose-b.html:464-474 */
const CX = 350, CY = 464, RX = 297, RT = 0.23607, RY = RX * RT;   // platter top
const BAND = 36, GAP = 4, BASE = 20, RB = RX + 12, RBY = RB * RT; // stepped plinth: band / recess / fixed base
const RR = 240, DS = 1.2, SMIN = 0.72;                            // figure ring radius, doily scale, back scale
const H0 = 272, P0 = 28;                                          // figure height (source 1100px), pole
const FOOT = CY + RR * RT;                                        // front figure foot y
const DWELL = 4600, TURN = 1900, CAMY = -26;
const ENTER_MS = 1300;                                            // the push-in, rose-b.html:851
const NARROW = 960, WIDE = 1096, STAGE_H = 620, THEATRE_LEFT = 396, THEATRE_W = 700;
const DUST_SEED = 20260927;                                       // the motes' seed: the same dust on every load

/* ---- cubic-bezier easing (for rAF), rose-b.html:477-490 ---- */
function bez(p1x, p1y, p2x, p2y) {
  const a = (a1, a2) => 1 - 3 * a2 + 3 * a1, b = (a1, a2) => 3 * a2 - 6 * a1, c = (a1) => 3 * a1;
  const calc = (t, a1, a2) => ((a(a1, a2) * t + b(a1, a2)) * t + c(a1)) * t;
  const slope = (t, a1, a2) => 3 * a(a1, a2) * t * t + 2 * b(a1, a2) * t + c(a1);
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const s = slope(t, p1x, p2x);
      if (Math.abs(s) < 1e-6) break;
      t -= (calc(t, p1x, p2x) - x) / s;
    }
    return calc(Math.min(1, Math.max(0, t)), p1y, p2y);
  };
}
const easeTurn = bez(0.62, 0, 0.28, 1), easeOut = bez(0.2, 0.7, 0.1, 1), easeEnter = bez(0.7, 0, 0.3, 1);
const smooth = (e0, e1, x) => { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };
const f2 = (v) => v.toFixed(2);
const mod = (a, n) => ((a % n) + n) % n;

/** mulberry32, a small seeded generator (https://gist.github.com/tommyettinger/46a874533244883189143505d203312c): the dust
 *  motes are decoration, but the same seed gives the same motes, so a screenshot can be compared with the last. */
function seeded(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ---- path helpers, rose-b.html:531-537 ---- */
const circ = (x, y, rx, ry) => `M${f2(x - rx)} ${f2(y)}a${f2(rx)} ${f2(ry)} 0 1 0 ${f2(2 * rx)} 0a${f2(rx)} ${f2(ry)} 0 1 0 ${f2(-2 * rx)} 0`;
/* polar point on a disc seen from above: angle 0 = towards the viewer (same convention as the figure ring) */
const P = (r, a) => `${f2(r * Math.sin(a))} ${f2(r * Math.cos(a))}`;
const PX = (r, a) => [r * Math.sin(a), r * Math.cos(a)];

/* ---- lace doily (drawn once, top-down, radius 190; turned by a transform), rose-b.html:539-578 ---- */
function doilyPaths() {
  let line = '', fill = '', dots = '', k, a, am, w, n;
  // scalloped edge (60 scallops), echoed inside; fill = the doily body
  n = 60;
  for (let pass = 0; pass < 2; pass++) {
    const r0 = pass ? 180 : 184, r1 = pass ? 193 : 200;
    let s = '';
    for (k = 0; k < n; k++) {
      a = k / n * TAU; am = (k + 0.5) / n * TAU;
      s += (k ? '' : `M${P(r0, a)}`) + `Q${P(r1, am)} ${P(r0, (k + 1) / n * TAU)}`;
    }
    line += `${s}Z`;
    if (!pass) fill += `${s}Z`;
  }
  for (k = 0; k < n; k++) { am = (k + 0.5) / n * TAU; const q = PX(196.5, am); dots += circ(q[0], q[1], 1.15, 1.15); }
  // eyelet ring
  for (k = 0; k < n; k++) { am = (k + 0.5) / n * TAU; const e = PX(171, am); line += circ(e[0], e[1], 2.3, 2.3); }
  line += circ(0, 0, 163, 163) + circ(0, 0, 160, 160);
  // band of pointed (gothic) arches, 24 round
  n = 24; w = TAU / n * 0.4;
  for (k = 0; k < n; k++) {
    a = k / n * TAU;
    line += `M${P(122, a - w)}L${P(145, a - w)}Q${P(155, a - w * 0.92)} ${P(158, a)}Q${P(155, a + w * 0.92)} ${P(145, a + w)}L${P(122, a + w)}`;
    line += `M${P(126, a - w * 0.5)}L${P(141, a - w * 0.5)}Q${P(148, a - w * 0.45)} ${P(151, a)}Q${P(148, a + w * 0.45)} ${P(141, a + w * 0.5)}L${P(126, a + w * 0.5)}`;
    const t = PX(158, a + TAU / n / 2); dots += circ(t[0], t[1], 0.9, 0.9);
  }
  line += circ(0, 0, 122, 122) + circ(0, 0, 118, 118);
  for (k = 0; k < 48; k++) { const dd = PX(111, k / 48 * TAU); dots += circ(dd[0], dd[1], 1, 1); }
  // rosette: 8 petals with a vein, 8 smaller petals between
  for (k = 0; k < 8; k++) {
    a = k / 8 * TAU;
    line += `M${P(18, a)}C${P(58, a - 0.36)} ${P(102, a - 0.2)} ${P(98, a)}C${P(102, a + 0.2)} ${P(58, a + 0.36)} ${P(18, a)}`;
    line += `M${P(30, a)}L${P(84, a)}`;
    const b2 = a + TAU / 16;
    line += `M${P(22, b2)}C${P(44, b2 - 0.26)} ${P(70, b2 - 0.14)} ${P(68, b2)}C${P(70, b2 + 0.14)} ${P(44, b2 + 0.26)} ${P(22, b2)}`;
  }
  line += circ(0, 0, 14, 14); dots += circ(0, 0, 4, 4);
  return { line, fill, dots };
}

/* ---- rose window tracery (only built when asked for), rose-b.html:625-643 ---- */
function windowPath() {
  let s = '', k, a, w;
  const OX = 350, OY = 176;
  const Q = (r, ang) => `${f2(OX + r * Math.sin(ang))} ${f2(OY - r * Math.cos(ang))}`;
  s += circ(OX, OY, 170, 170) + circ(OX, OY, 163, 163);
  for (k = 0; k < 16; k++) { a = (k + 0.5) / 16 * TAU; s += circ(OX + 151 * Math.sin(a), OY - 151 * Math.cos(a), 9, 9); }
  s += circ(OX, OY, 140, 140);
  w = TAU / 12 * 0.36;
  for (k = 0; k < 12; k++) {
    a = k / 12 * TAU;
    s += `M${Q(50, a - w * 1.6)}L${Q(112, a - w)}Q${Q(128, a - w * 0.8)} ${Q(134, a)}Q${Q(128, a + w * 0.8)} ${Q(112, a + w)}L${Q(50, a + w * 1.6)}`;
    s += circ(OX + 104 * Math.sin(a), OY - 104 * Math.cos(a), 7, 7);
    s += `M${Q(46, a + TAU / 24)}L${Q(138, a + TAU / 24)}`;
  }
  s += circ(OX, OY, 46, 46);
  for (k = 0; k < 6; k++) { a = k / 6 * TAU; s += circ(OX + 20 * Math.sin(a), OY - 20 * Math.cos(a), 14, 14); }
  s += circ(OX, OY, 6, 6);
  return s;
}

/** Everything the prototype's IIFE did once, for one <vc-turntable> and its prepared colourways. Returns attach() / detach():
 *  the observers, the document listeners and the timers, which come and go with the element's place in the page. */
function createTurntable(root, rows) {
  const $ = (s) => root.querySelector(s);
  // the art's keys are the prototype's field names (d.hem, d.hx …); the row's own keys (name, w, h …) never clash with them
  const CW = rows.map((r) => ({ ...r.art, ...r }));
  const N = CW.length, STEP = TAU / N, OF = roman(N);
  const mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  let reduce = !!(mq && mq.matches);
  let scale = 1;

  /* ---- build figures, rose-b.html:492-526 ---- */
  const figs = $('.vc-tt__figs'), refl = $('.vc-tt__refl'), shadows = $('.vc-tt__shadows');
  // the front figure as the section printed it (.vc-tt__fig > .vc-tt__fi > pole, foot, <img data-vc-front>) and its copy (.vc-tt__v): the
  // colourway (data-vc-front) is where the turntable starts; a print that is not among the built colourways is dropped (with its copy), so
  // nothing stray stays on the stage
  const printed = $('.vc-tt__figs img[data-vc-front]');
  const at = printed ? CW.findIndex((d) => d.id === printed.dataset.vcFront) : -1;
  const printedFig = at >= 0 ? printed.closest('.vc-tt__fig') : null;
  if (printed && at < 0) { printed.closest('.vc-tt__fig').remove(); root.querySelectorAll('.vc-tt__swap > .vc-tt__v').forEach((v) => v.remove()); }
  const start = at >= 0 ? at : Math.min(2, N - 1);
  function buildInner(d, isRefl, first) {
    const k = H0 / 1100 * d.f;
    const adopt = first && !isRefl && printedFig;   // the printed figure: its fi, pole, foot and <img> (alt, srcset, sizes, src, priority) stay where they are
    const fi = adopt ? printedFig.querySelector('.vc-tt__fi') : document.createElement('div');
    fi.className = 'vc-tt__fi';
    const W = d.w * k, hemY = d.hem * k;
    fi.style.left = `${-d.hx * k}px`; fi.style.width = `${W}px`; fi.style.height = `${hemY + P0}px`;
    const img = adopt ? printed : document.createElement('img');
    if (!adopt) {
      img.alt = isRefl ? '' : `${d.name} colourway`; img.draggable = false; img.decoding = 'async';
      if (first && !isRefl) img.fetchPriority = 'high';
      else { img.loading = 'lazy'; img.fetchPriority = 'low'; }   // set before src: behind or beside the front picture, and a reflection is display:none until its figure is in front
    }
    img.style.width = `${W}px`; img.style.height = `${d.h * k}px`;
    const st = d.stand, px = (v, n) => `${(v / n * 100).toFixed(2)}%`;
    [['--t0', st[3] - 4, d.h], ['--t1', st[3] + 4, d.h], ['--hcx', (st[0] + st[1]) / 2, d.w],
      ['--hrx', (st[1] - st[0]) / 2 * 1.14, d.w], ['--hry', st[2] * 1.06, d.h]]
      .forEach((v) => img.style.setProperty(v[0], px(v[1], v[2])));
    img.style.clipPath = `inset(0 ${((d.cr || 0) / d.w * 100).toFixed(2)}% ${((d.h - d.hem - 1) / d.h * 100).toFixed(2)}% 0)`;
    // sizes / srcset before src: the picture is fetched once, at the size the figure is drawn at (d.sizes: the box at each layout's scale;
    // the box alone, W wide, is the answer when the section gave none)
    if (!adopt) {
      if (d.srcset) { img.sizes = d.sizes || `${Math.ceil(W)}px`; img.srcset = d.srcset; }
      img.src = d.img;
      fi.appendChild(img);
    }
    if (!isRefl) {
      const pole = adopt ? fi.querySelector('.vc-tt__pole') : document.createElement('span'); pole.className = 'vc-tt__pole';
      pole.style.left = `${d.hx * k}px`; pole.style.height = `${P0 + 2}px`;
      const foot = adopt ? fi.querySelector('.vc-tt__foot') : document.createElement('span'); foot.className = 'vc-tt__foot'; foot.style.left = `${d.hx * k}px`;
      if (!adopt) { fi.insertBefore(foot, img); fi.insertBefore(pole, foot); }
    }
    return fi;
  }
  const namesG = $('.vc-tt__names');
  const F = CW.map((d, i) => {
    const el = i === start && printedFig ? printedFig : document.createElement('div'); el.className = 'vc-tt__fig'; el.dataset.i = i;
    const fi = buildInner(d, false, i === start);
    if (el !== printedFig) { el.appendChild(fi); figs.insertBefore(el, i < start ? printedFig : null); }
    const r = document.createElement('div'); r.className = 'vc-tt__fig'; r.appendChild(buildInner(d, true, false)); refl.appendChild(r);
    const sh = document.createElement('div'); sh.className = 'vc-tt__shadow'; shadows.appendChild(sh);
    const tx = document.createElementNS(SVGNS, 'text');
    tx.setAttribute('text-anchor', 'middle'); tx.setAttribute('dominant-baseline', 'central');
    tx.textContent = d.name.toUpperCase(); namesG.appendChild(tx);
    return { el, r, sh, tx, c: {} };
  });
  /* write a style/attr value only when its string changed (no restyle at rest) */
  const put = (f, key, node, prop, val) => { if (f.c[key] !== val) { f.c[key] = val; node.style[prop] = val; } };
  const att = (f, key, node, name, val) => { if (f.c[key] !== val) { f.c[key] = val; node.setAttribute(name, val); } };

  /* ---- lace doily ---- */
  const dp = doilyPaths();
  $('.vc-tt__dline').setAttribute('d', dp.line); $('.vc-tt__dfill').setAttribute('d', dp.fill); $('.vc-tt__ddots').setAttribute('d', dp.dots);
  const doilyG = $('.vc-tt__doily');

  /* ---- plinth geometry (static): every coordinate derives from CX/CY/RX/RT so one projection rules, rose-b.html:581-618 ---- */
  (function plinth() {
    const A = (n, o) => { const e = root.querySelectorAll(n); for (let i = 0; i < e.length; i++) for (const k in o) e[i].setAttribute(k, o[k]); };
    const ell = (n, cx, cy, rx, ry) => A(n, { cx: f2(cx), cy: f2(cy), rx: f2(rx), ry: f2(ry) });
    const arcF = (y, rx, ry) => `M${f2(CX - rx)} ${f2(y)}A${f2(rx)} ${f2(ry)} 0 0 0 ${f2(CX + rx)} ${f2(y)}`;
    const bandP = (y0, y1, rx, ry) => `${arcF(y0, rx, ry)}V${f2(y1)}A${f2(rx)} ${f2(ry)} 0 0 1 ${f2(CX - rx)} ${f2(y1)}Z`;
    const yB = CY + BAND, yL = yB + GAP, yF = yL + BASE, RN = RX - 5;
    // fixed base: ledge top at yL, 20px side down to the floor
    ell('.vc-tt__ledge', CX, yL, RB, RBY);
    A('.vc-tt__baseside, .vc-tt__baselit, .vc-tt__baseshade', { d: bandP(yL, yF, RB, RBY) });
    A('.vc-tt__lip', { d: arcF(yL, RB, RBY) });
    A('.vc-tt__floorline', { d: arcF(yF, RB, RBY) });
    // recess: a narrower dark neck between the band and the base
    A('.vc-tt__recess', { d: `${arcF(yB, RX, RY)}L${f2(CX + RN)} ${f2(yL)}A${f2(RN)} ${f2(RN * RT)} 0 0 1 ${f2(CX - RN)} ${f2(yL)}Z` });
    A('.vc-tt__recesssh', { d: arcF(yB + 2, RX - 2, RY) });
    // turning band + engraved hairlines + lips
    A('.vc-tt__band', { d: bandP(CY, yB, RX, RY) });
    A('.vc-tt__eng1', { d: arcF(CY + 6, RX, RY) }); A('.vc-tt__eng2', { d: arcF(yB - 6, RX, RY) });
    A('.vc-tt__toplip', { d: arcF(CY, RX, RY) }); A('.vc-tt__botlip', { d: arcF(yB, RX, RY) });
    A('.vc-tt__catch', { d: arcF(yF + 8, RB + 8, RBY + 2) });
    // platter top, rims, pools, floor
    ell('.vc-tt__top', CX, CY, RX, RY); ell('.vc-tt__rim1', CX, CY, RX, RY); ell('.vc-tt__rim2', CX, CY, RX - 13, (RX - 13) * RT);
    ell('.vc-tt__pool', CX, FOOT - 6, 176, 30);
    ell('.vc-tt__floorlit', CX, yF - 10, 400, 58);
    ell('.vc-tt__floorsh', CX, yF - 22, RB + 20, 40);
    ell('.vc-tt__halo', CX, FOOT - 150, 180, 214);
    // gradients laid out in user space follow the geometry
    A('#vctt-rimdepth', { y1: f2(CY - RY), y2: f2(CY + RY) });
    A('#vctt-beaddepth', { y1: f2(CY - RY), y2: f2(CY + RY) });
    A('#vctt-dlit', { cx: CX, cy: f2(FOOT - 18), r: 320, gradientTransform: `translate(0 ${f2(FOOT - 18)}) scale(1 .42) translate(0 ${f2(18 - FOOT)})` });
    A('#vctt-bandside, #vctt-bandlit, #vctt-goldfront2', { x1: f2(CX - RX), x2: f2(CX + RX) });
    A('#vctt-baseside, #vctt-goldbase', { x1: f2(CX - RB), x2: f2(CX + RB) });
    // keyboard focus arch around the front figure
    const fy = FOOT + 8, ar = 124, ay = FOOT - H0 - 40 + ar;    // crown clears the front collar by ~40px
    A('.vc-tt__focus', { d: `M${CX - ar} ${f2(fy)}V${f2(ay)}A${ar} ${ar} 0 0 1 ${CX + ar} ${f2(ay)}V${f2(fy)}` });
    A('#vctt-focus', { y1: f2(ay - ar), y2: f2(fy) });
    refl.style.clipPath = `ellipse(${f2(RX)}px ${f2(RY)}px at ${CX}px ${CY}px)`;
  }());

  /* ---- rose window tracery (only built when the section asks for it) ---- */
  if (root.dataset.backdrop === 'window') {
    root.classList.add('is-window');
    $('.vc-tt__tracery').setAttribute('d', windowPath());
  }

  /* ---- dust motes: 8, r >= .9, CSS-driven (the loops are in the stylesheet; nothing runs per frame), rose-b.html:645-659 ---- */
  if (!reduce) {
    const dust = $('.vc-tt__dust'), rnd = seeded(DUST_SEED);
    for (let mi = 0; mi < 8; mi++) {
      const u = (mi + rnd() * 0.8) / 8 * 2 - 1;
      const m = document.createElement('i'), mb = document.createElement('b');
      m.style.left = `${(350 + u * 58).toFixed(1)}px`;
      m.style.setProperty('--p', `${(30 + rnd() * 30).toFixed(1)}s`);
      m.style.setProperty('--dl', `${(-rnd() * 60).toFixed(1)}s`);
      m.style.setProperty('--sw', `${(7 + rnd() * 6).toFixed(1)}s`);
      m.style.setProperty('--s', `${(1.8 + rnd() * 0.6).toFixed(2)}px`);
      m.style.setProperty('--a', (0.16 + rnd() * 0.22 * (1 - Math.abs(u) * 0.6)).toFixed(2));
      m.appendChild(mb); dust.appendChild(m);
    }
  }

  /* ---- index ---- */
  const nums = $('.vc-tt__nums'), seg = $('.vc-tt__seg'), segFill = $('.vc-tt__seg i');
  const idxBtns = CW.map((d, i) => {
    const b = document.createElement('button'); b.type = 'button'; b.textContent = d.num || String(i + 1);
    b.setAttribute('aria-label', `Colourway ${d.num || i + 1} of ${OF}, ${d.name}`);
    b.addEventListener('click', () => { userChose(); turnTo(i); });
    nums.appendChild(b); return b;
  });

  /* ---- copy swapping, rose-b.html:670-691 ---- */
  function swap(el, html) {
    el.querySelectorAll('.vc-tt__v:not(.is-out)').forEach((o) => { o.classList.add('is-out'); setTimeout(() => o.remove(), 700); });
    const v = document.createElement('span'); v.className = 'vc-tt__v is-pre'; v.innerHTML = html; el.appendChild(v);
    v.getBoundingClientRect();
    requestAnimationFrame(() => { requestAnimationFrame(() => { v.classList.remove('is-pre'); }); });
  }
  const titleEl = $('.vc-tt__titlebox'), fabEl = $('.vc-tt__fabric'), descEl = $('.vc-tt__desc'), feature = $('.vc-tt__feature');
  const cta = $('.vc-tt__cta'), pauseBtn = $('.vc-tt__pause'), theatre = $('.vc-tt__theatre'), inner = $('.vc-tt__inner');
  theatre.tabIndex = 0;   // the section prints the stage without: before this module has built it there is nothing to operate
  let shown = -1;
  function setCopy(i) {
    if (i === shown) return;
    const keep = shown < 0 && printedFig && i === at;   // the section printed this colourway's copy (.vc-tt__v): it stays, nothing to fade
    shown = i;
    const d = CW[i], sw = d.sweet ? ' is-sweet' : '';
    if (!keep) {
      swap(titleEl, `<span class="vc-tt__chapter${sw}">`
        + (d.num ? `<span class="vc-tt__num">${escapeHtml(d.num)}</span><span class="vc-tt__of"><i>of</i><b>${OF}</b></span><span class="vc-tt__bar"></span>` : '')
        + `<span class="vc-tt__mood${sw}">${d.moodLabel}</span></span>`
        + `<span class="vc-tt__name">${escapeHtml(d.name).replace(' ', ' <br>')}</span>`);
      swap(fabEl, d.fabric.map(escapeHtml).join('<span class="vc-tt__dot"></span>'));
      swap(descEl, escapeHtml(d.desc));
    }
    idxBtns.forEach((b, j) => { if (j === i) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current'); });
    seg.style.transform = `translateX(${i * 38}px)`;
    // the link follows the front colourway: its collection, with its mood for the lace curtain (Task 16)
    cta.setAttribute('href', d.url); cta.dataset.mood = d.mood; cta.setAttribute('aria-label', `Enter the chapter, ${d.name}`);
  }
  /* announce only after a user action, never during autoplay (WCAG 4.1.3 / 2.2.2) */
  const markUser = () => feature.setAttribute('aria-live', 'polite');

  /* ---- state, rose-b.html:693-712 ---- */
  let pos = start, anim = null, drag = null, paused = false, hot = false, userPaused = false;
  let autoplay = !reduce;
  let enter = 0, enterAnim = null, visible = true, going = false;

  function goTo(target, dur, ease) {
    if (enterAnim || enter > 0) return;               // held while the push-in runs: the link followed is the colourway entered
    if (reduce) dur = Math.min(dur, 1);
    anim = { from: pos, to: target, t0: performance.now(), dur, ease: ease || easeTurn };
    syncAuto(); kick();
  }
  function turnTo(i) {
    const cur = anim ? anim.to : Math.round(pos);
    const delta = mod(i - cur + 3, N) - 3;
    goTo(cur + delta, TURN * (Math.abs(delta) > 1 ? 1.25 : 1));
  }
  const frontIndex = () => mod(Math.round(anim ? anim.to : pos), N);

  /* ---- platter ornament (turns with the stage; redrawn only when pos changes) ----
     beads on the top rim + the doily rotation. (v4: the hanging lace edge and pearls are gone, fix 7) */
  const beadsEl = $('.vc-tt__beads');
  function drawOrnament(phase) {
    let s = '';
    const n = 120;
    for (let i = 0; i < n; i++) {
      const a = i / n * TAU - phase, c = Math.cos(a);
      const x = CX + (RX - 6.5) * Math.sin(a), y = CY + (RY - 1.1) * c, r = 0.4 + 0.5 * (1 + c) / 2;
      s += circ(x, y, r, r * 0.8);
    }
    beadsEl.setAttribute('d', s);
    doilyG.setAttribute('transform', `translate(${CX} ${CY}) scale(${DS} ${(DS * RT).toFixed(5)}) rotate(${(phase * 180 / Math.PI).toFixed(3)})`);
  }

  const tints = root.querySelectorAll('.vc-tt__back .vc-tt__tint'), tints2 = root.querySelectorAll('.vc-tt__front .vc-tt__tint2');
  const lightG = $('.vc-tt__light'), halo = $('.vc-tt__halo'), cam = $('.vc-tt__cam');
  let lastHex = '', lastHalo = '';

  /* ---- render (only called when pos / enter / hot changed), rose-b.html:732-799 ---- */
  let lastPos = NaN, lastEnter = NaN, lastHot = null;
  function render() {
    let tr = 0, tg = 0, tb = 0, tw = 0, th0 = 0, am = 0, aw = 0;
    const fi = frontIndex();
    for (let j = 0; j < N; j++) { const cj = Math.cos((j - pos) * STEP), wj = Math.pow((1 + cj) / 2, 10); am += CW[j].amb * wj; aw += wj; }
    am /= aw;                                      // blended across a turn, like the chapter light
    for (let i = 0; i < N; i++) {
      let th = (i - pos) * STEP; th = Math.atan2(Math.sin(th), Math.cos(th));
      const sn = Math.sin(th), c = Math.cos(th), d = (1 + c) / 2, D = CW[i];
      // stand ON the platter ellipse (same projection as doily/beads), mild separate size falloff
      const s = SMIN + (1 - SMIN) * d;
      const x = CX + RR * sn, y = CY + RR * RT * c;
      const isF = i === fi ? 1 : 0, nE = enter * (1 - isF);
      // atmosphere, not blackening: brightness floors at .55, depth goes into opacity (+ desaturation, light blur)
      const d6 = Math.pow(d, 6), br = (0.53 + 0.47 * d6) * (am + (1 - am) * d6) * (1 + (hot && isF ? 0.07 : 0) + enter * isF * 0.08);
      const bk = smooth(-0.05, -0.55, c);               // 0 front/sides -> 1 behind
      const bl = 0.5 * Math.pow(1 - d, 1.5) + 0.9 * bk + 2.5 * nE;
      const sat = 0.78 + 0.22 * Math.pow(d, 2);          // keep colour at the back: hints of colour, not grey slabs
      // sides stay opaque (no double exposure through them); back quarters fall to ~.2; the one directly behind fades out
      const op = (0.2 + 0.8 * smooth(0.22, 0.72, d)) * (1 - 0.88 * nE) * smooth(-1, -0.72, c);
      const lw = Math.pow(d, 10);
      tr += D.light[0] * lw; tg += D.light[1] * lw; tb += D.light[2] * lw; th0 += D.halo * lw; tw += lw;
      const f = F[i];
      const tf = `translate3d(${f2(x)}px,${f2(y)}px,0) scale(`;
      put(f, 't', f.el, 'transform', `${tf}${s.toFixed(4)})`);
      put(f, 'z', f.el, 'zIndex', String(Math.round(1000 + 1000 * c)));
      const ct = D.con * (1 - 0.14 * (1 - d * d));      // atmosphere also softens contrast with depth
      put(f, 'f', f.el, 'filter', `brightness(${(br * D.expo).toFixed(3)}) contrast(${ct.toFixed(3)}) saturate(${sat.toFixed(3)})${bl > 0.05 ? ` blur(${bl.toFixed(2)}px)` : ''}`);
      put(f, 'o', f.el, 'opacity', op.toFixed(3));
      put(f, 'v', f.el, 'visibility', op > 0.005 ? 'visible' : 'hidden');
      put(f, 'p', f.el, 'pointerEvents', d > 0.15 ? 'auto' : 'none');
      // mirror reflection in the lacquer: front figure only
      const ro = 0.3 * Math.pow(d, 14) * (1 - nE);
      put(f, 'rd', f.r, 'display', ro > 0.01 ? '' : 'none');
      if (ro > 0.01) {
        put(f, 'rt', f.r, 'transform', `${tf}${s.toFixed(4)},${(-s).toFixed(4)})`);
        put(f, 'ro', f.r, 'opacity', ro.toFixed(3));
        put(f, 'rf', f.r, 'filter', `brightness(${(br * D.expo * 0.9).toFixed(3)}) blur(1.2px)`);
      }
      put(f, 'st', f.sh, 'transform', `translate3d(${f2(x)}px,${f2(y + 1)}px,0) scale(${s.toFixed(3)})`);
      put(f, 'so', f.sh, 'opacity', ((0.5 + 0.5 * d) * op).toFixed(3));
      // engraved name on the band at its figure's band ANGLE (turns with the beads); front name only
      const no = smooth(0.8, 1, c) * (1 - nE);
      att(f, 'nd', f.tx, 'display', no > 0.01 ? 'inline' : 'none');
      if (no > 0.01) {
        const ny = CY + RY * c + BAND / 2 + 0.3, sk = Math.atan(-RT * Math.tan(th)) * 180 / Math.PI;
        att(f, 'nt', f.tx, 'transform', `translate(${f2(CX + RX * sn)} ${f2(ny)}) skewY(${sk.toFixed(2)}) scale(${c.toFixed(3)} 1)`);
        att(f, 'no', f.tx, 'opacity', (no * 0.85).toFixed(3));
      }
    }
    const col = [tr / tw, tg / tw, tb / tw].map(Math.round), hex = `rgb(${col.join(',')})`;
    if (hex !== lastHex) {
      lastHex = hex;
      for (let t = 0; t < tints.length; t++) tints[t].setAttribute('stop-color', hex);
      for (let t = 0; t < tints2.length; t++) tints2[t].setAttribute('stop-color', hex);
      root.style.setProperty('--mote', col.join(','));
    }
    const hv = (th0 / tw * (1 + enter * 0.25)).toFixed(3);
    if (hv !== lastHalo) { lastHalo = hv; halo.setAttribute('opacity', hv); }
    if (enter !== lastEnter) {
      lightG.style.opacity = (1 + enter * 0.5).toFixed(3);
      cam.style.transform = `translate3d(0,${CAMY}px,0)${enter > 0 ? ` scale(${(1 + 0.05 * easeOut(enter)).toFixed(4)})` : ''}`;
    }
    if (pos !== lastPos) drawOrnament(pos * STEP);
    lastPos = pos; lastEnter = enter; lastHot = hot;
    setCopy(frontIndex());
  }

  /* ---- loop: runs only while something moves; idle = zero frames, rose-b.html:801-818 ---- */
  let raf = 0;
  function kick() { if (!raf) raf = requestAnimationFrame(tick); }
  function tick(now) {
    raf = 0;
    if (anim) {
      const t = Math.min(1, (now - anim.t0) / anim.dur);
      pos = anim.from + (anim.to - anim.from) * anim.ease(t);
      if (t >= 1) { pos = anim.to; anim = null; syncAuto(); }
    }
    if (enterAnim) {
      const te = Math.min(1, (now - enterAnim.t0) / enterAnim.dur);
      enter = enterAnim.from + (enterAnim.to - enterAnim.from) * easeEnter(te);
      if (te >= 1) { const done = enterAnim.done; enterAnim = null; if (done) done(); syncAuto(); }
    }
    if (pos !== lastPos || enter !== lastEnter || hot !== lastHot) render();
    if (anim || enterAnim) kick();
  }

  /* ---- autoplay: a timeout + a CSS-transitioned gold fill (no rAF while dwelling), rose-b.html:820-844 ---- */
  let dwellT = 0;
  const autoOn = () => autoplay && !userPaused && !paused && !drag && !anim && !enterAnim && enter === 0 && visible && !document.hidden;
  function fill(on) {
    if (on) {
      segFill.style.transition = 'none'; segFill.style.transform = 'scaleX(0)'; segFill.getBoundingClientRect();
      segFill.style.transition = `transform ${DWELL}ms linear`; segFill.style.transform = 'scaleX(1)';
    } else { segFill.style.transition = 'transform .6s cubic-bezier(.2,.7,.1,1)'; segFill.style.transform = 'scaleX(0)'; }
  }
  function syncAuto() {
    const on = autoOn();
    if (on && !dwellT) {
      fill(true);
      dwellT = setTimeout(() => { dwellT = 0; fill(false); goTo(Math.round(pos) + 1, TURN); }, DWELL);
    } else if (!on && dwellT) { clearTimeout(dwellT); dwellT = 0; fill(false); }
  }
  function setUserPaused(v) {
    userPaused = v; pauseBtn.textContent = v ? 'Play' : 'Pause';
    pauseBtn.setAttribute('aria-label', v ? 'Play the colourways' : 'Pause the colourways');
    if (!v) autoplay = true;
    syncAuto();
  }
  /* an explicit choice (numeral, drag, figure, arrow keys, enter) holds that colourway: autoplay stops until Play */
  function userChose() { markUser(); if (!userPaused) setUserPaused(true); }
  pauseBtn.addEventListener('click', () => { markUser(); setUserPaused(!userPaused); });

  /* ---- enter chapter, rose-b.html:846-861: the push-in, then the front colourway's link ---- */
  function enterChapter() {
    if (enterAnim || enter > 0) return;
    root.classList.add('is-entering');
    enterAnim = { from: 0, to: 1, t0: performance.now(), dur: reduce ? 1 : ENTER_MS, done: follow };
    syncAuto(); kick();
  }
  /* The push-in is done: follow the link with a real click on it, so the lace curtain (Task 16) sees it as it sees any link
     click on an a[data-mood]; `going` lets this click past the handler below. */
  function follow() {
    going = true;
    try { cta.click(); } finally { going = false; }
  }
  /* A page restored from the back/forward cache comes back as it was left, pushed in; put it back at rest. */
  function onPageShow(e) {
    if (!e.persisted || !(enter > 0 || enterAnim)) return;
    enterAnim = null; enter = 0; root.classList.remove('is-entering');
    kick(); syncAuto();
  }
  cta.addEventListener('click', (e) => {
    if (going || e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;   // a new tab or window: the browser's own
    if (cta.target && cta.target !== '_self') return;
    e.preventDefault(); userChose(); enterChapter();
  });

  /* ---- interaction, rose-b.html:863-907 ---- */
  function setPaused(v) { paused = v; root.classList.toggle('is-paused', v); syncAuto(); }
  function setHot(v) { if (hot === v) return; hot = v; root.classList.toggle('is-hot', v); kick(); }
  theatre.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') setPaused(true); });
  theatre.addEventListener('pointerleave', () => { setHot(false); if (document.activeElement !== theatre) setPaused(false); });
  theatre.addEventListener('pointermove', (e) => {
    if (drag) {
      const dx = e.clientX - drag.x0;
      if (Math.abs(dx) > 4 && !drag.moved) { drag.moved = true; setHot(false); userChose(); }
      if (drag.moved) {
        const now = performance.now(), np = drag.p0 - dx / (RR * scale) / STEP;
        drag.v = (np - pos) / Math.max(1, now - drag.t); drag.t = now; pos = np; kick();
      }
      return;
    }
    const fig = e.target.closest && e.target.closest('.vc-tt__figs .vc-tt__fig');
    setHot(!!fig && +fig.dataset.i === frontIndex() && !anim);
  });
  theatre.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || enterAnim || enter > 0) return;
    const fig = e.target.closest('.vc-tt__figs .vc-tt__fig');
    drag = { x0: e.clientX, p0: pos, moved: false, v: 0, t: performance.now(), fig: fig ? +fig.dataset.i : -1 };
    anim = null; theatre.setPointerCapture(e.pointerId); theatre.classList.add('is-drag'); syncAuto();
  });
  function endDrag() {
    if (!drag) return;
    const dg = drag; drag = null; theatre.classList.remove('is-drag');
    if (!dg.moved) {
      if (dg.fig >= 0) {
        userChose();
        if (dg.fig === mod(Math.round(pos), N) && Math.abs(pos - Math.round(pos)) < 0.05) enterChapter(); else turnTo(dg.fig);
      } else if (Math.abs(pos - Math.round(pos)) > 0.001) goTo(Math.round(pos), 900, easeOut);
      else syncAuto();
      return;
    }
    const target = Math.round(pos + Math.max(-1.5, Math.min(1.5, dg.v * 260)));
    goTo(target, 1200, easeOut);
  }
  theatre.addEventListener('pointerup', endDrag);
  theatre.addEventListener('pointercancel', endDrag);
  theatre.addEventListener('keydown', (e) => {
    const cur = anim ? anim.to : Math.round(pos);
    if (e.key === 'ArrowRight') { e.preventDefault(); userChose(); goTo(cur + 1, TURN * 0.8); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); userChose(); goTo(cur - 1, TURN * 0.8); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); userChose(); enterChapter(); }
  });
  theatre.addEventListener('focus', () => setPaused(true));
  theatre.addEventListener('blur', () => setPaused(false));

  /* ---- sizing (graceful down to 390px, 16px gutters, centred on the plinth), rose-b.html:909-924 ---- */
  function layout() {
    const w = root.clientWidth, narrow = w < NARROW;
    root.classList.toggle('is-narrow', narrow);
    if (narrow) {
      // v4 (fix 10): keep the product large -- scale ~.84 at 390px and let the plinth bleed off both
      // sides (the stage clips), centred on the front figure; crop the empty top of the 620 stage.
      const Y0 = 176, Y1 = 598;
      scale = Math.min(1, w / 465);
      theatre.style.height = `${((Y1 - Y0) * scale).toFixed(1)}px`;
      inner.style.transform = `translate(${(w / 2 - CX * scale).toFixed(1)}px,${(-Y0 * scale).toFixed(1)}px) scale(${scale.toFixed(4)})`;
    } else if (w < WIDE) {
      // theme: the room left of the copy is less than the theatre's 700px: scale it to fit, standing on the stage's floor
      scale = Math.max(0.5, (w - THEATRE_LEFT) / THEATRE_W);
      theatre.style.height = '';
      inner.style.transform = `translate(0,${(STAGE_H * (1 - scale)).toFixed(1)}px) scale(${scale.toFixed(4)})`;
    } else { scale = 1; theatre.style.height = ''; inner.style.transform = ''; }
  }

  /* ---- motion preference, read live: the autoplay follows it ---- */
  function onMotion() {
    reduce = !!(mq && mq.matches);
    if (reduce && !userPaused) setUserPaused(true);   // no autoplay under reduced motion; Play is still the visitor's to press
  }

  let ro = null, io = null, built = false;
  /* the observers, the document listeners and the timers: taken up when the element is in the page, let go of when it leaves */
  function attach() {
    layout();
    if (!built) {
      if (reduce) { userPaused = true; pauseBtn.textContent = 'Play'; pauseBtn.setAttribute('aria-label', 'Play the colourways'); }
      else pauseBtn.setAttribute('aria-label', 'Pause the colourways');
      render();
      root.classList.add('is-built');
      built = true;
    }
    if (window.ResizeObserver) { ro = new ResizeObserver(layout); ro.observe(root); } else window.addEventListener('resize', layout);
    if (window.IntersectionObserver) {
      io = new IntersectionObserver((en) => {
        visible = en[en.length - 1].isIntersecting; root.classList.toggle('is-offscreen', !visible); syncAuto();
      });
      io.observe(root);
    }
    document.addEventListener('visibilitychange', syncAuto);
    window.addEventListener('pageshow', onPageShow);
    if (mq && mq.addEventListener) mq.addEventListener('change', onMotion);
    if (anim || enterAnim) kick();      // moved in the page in mid-turn: detach() cancelled the frame the turn was waiting for
    syncAuto();
  }
  function detach() {
    if (ro) { ro.disconnect(); ro = null; } else window.removeEventListener('resize', layout);
    if (io) { io.disconnect(); io = null; }
    document.removeEventListener('visibilitychange', syncAuto);
    window.removeEventListener('pageshow', onPageShow);
    if (mq && mq.removeEventListener) mq.removeEventListener('change', onMotion);
    if (dwellT) { clearTimeout(dwellT); dwellT = 0; }
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
  }
  return { attach, detach };
}

export class VcTurntable extends HTMLElement {
  connectedCallback() {
    if (this.turntable) { this.turntable.attach(); return; }   // moved in the page: disconnectedCallback let go of the observers
    const json = this.querySelector('script[data-vc-colourways]');
    let raw = [];
    try { raw = JSON.parse(json ? json.textContent : '[]'); } catch (err) { console.warn('[vc-turntable]', err); }
    const rows = prepareTurntable(raw, this.dataset.seriesHandle || '');
    if (rows.length < 2) { this.classList.add('is-fallback'); return; }   // nothing to turn: the section leaves itself out
    this.turntable = createTurntable(this, rows);
    this.turntable.attach();
  }

  disconnectedCallback() {
    if (this.turntable) this.turntable.detach();
  }
}

if (!customElements.get('vc-turntable')) customElements.define('vc-turntable', VcTurntable);
